import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const DEFAULT_ORDER = [
  '/',
  '/payments',
  '/customers',
  '/database',
  '/money-flow',
  '/expenses',
  '/reports',
  '/balances',
  '/settings',
];

export function useNavOrder() {
  const { user } = useAuth();
  const [order, setOrder] = useState<string[]>(DEFAULT_ORDER);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!user) return;

    supabase
      .from('user_preferences')
      .select('nav_order')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.nav_order && Array.isArray(data.nav_order)) {
          const saved = data.nav_order as string[];
          const missing = DEFAULT_ORDER.filter(r => !saved.includes(r));
          setOrder([...saved, ...missing]);
        }
        setLoaded(true);
      });
  }, [user]);

  const persist = useCallback(
    (newOrder: string[]) => {
      if (!user) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);

      saveTimer.current = setTimeout(async () => {
        const { data: existing } = await supabase
          .from('user_preferences')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (existing) {
          await supabase
            .from('user_preferences')
            .update({ nav_order: newOrder, updated_at: new Date().toISOString() })
            .eq('user_id', user.id);
        } else {
          await supabase
            .from('user_preferences')
            .insert({ user_id: user.id, nav_order: newOrder });
        }
      }, 500);
    },
    [user],
  );

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setOrder(prev => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const resetOrder = useCallback(() => {
    setOrder(DEFAULT_ORDER);
    persist(DEFAULT_ORDER);
  }, [persist]);

  return { order, reorder, resetOrder, loaded };
}
